import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import styles from './PaymentSettings.module.css';
import Button from '../../../../components/Button/Button';
import StripeProvider from '../../../../components/Stripe/StripeProvider';
import StripePaymentForm from '../../../../components/Stripe/StripePaymentForm';
import { useStripePayment } from '../../../../hooks/useStripePayment';
import { useToast } from '../../../../contexts/ToastContext';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { StripePaymentMethodDto } from '../../../../services/api';

const CARD_BRAND_LABELS: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    jcb: 'JCB',
    unionpay: 'UnionPay',
    diners: 'Diners Club',
};

const PaymentSettingsInner: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { t } = useLanguage();
    const { loading, loadPaymentMethods, removePaymentMethod, initiatePayment } = useStripePayment();

    const [paymentMethods, setPaymentMethods] = useState<StripePaymentMethodDto[]>([]);
    const [isAdding, setIsAdding] = useState(false);
    const [clientSecret, setClientSecret] = useState<string | null>(null);

    const fetchPaymentMethods = useCallback(async () => {
        try {
            const methods = await loadPaymentMethods();
            setPaymentMethods(methods);
        } catch {
            // error state handled by hook
        }
    }, [loadPaymentMethods]);

    useEffect(() => {
        fetchPaymentMethods();
    }, [fetchPaymentMethods]);

    const handleAddCard = async () => {
        try {
            // Use a setup intent amount of 0 (or small verification amount) — backend determines this
            const intent = await initiatePayment(0, 'usd', { purpose: 'save_payment_method' });
            setClientSecret(intent.clientSecret);
            setIsAdding(true);
        } catch {
            showToast(t('opay.card.setupFailed'), 'error');
        }
    };

    const handlePaymentSuccess = async (paymentIntentId: string) => {
        showToast(t('opay.card.added'), 'success');
        setIsAdding(false);
        setClientSecret(null);
        await fetchPaymentMethods();
    };

    const handlePaymentError = (message: string) => {
        showToast(message, 'error');
    };

    const handleRemove = async (paymentMethodId: string) => {
        try {
            await removePaymentMethod(paymentMethodId);
            showToast(t('opay.card.removed'), 'success');
            setPaymentMethods(prev => prev.filter(m => m.id !== paymentMethodId));
        } catch {
            showToast(t('opay.card.removeFailed'), 'error');
        }
    };

    const handleCancel = () => {
        setIsAdding(false);
        setClientSecret(null);
    };

    const brandLabel = (brand: string) => CARD_BRAND_LABELS[(brand ?? '').toLowerCase()] ?? t('opay.method.card');

    return (
        <div className={styles.paymentPage}>
            <div className={styles.header}>
                <button
                    type="button"
                    className={styles.backButton}
                    onClick={() => navigate('/settings')}
                    aria-label={t('oset.backToSettings')}
                >
                    <svg aria-hidden="true" focusable="false" width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect style={{ fill: 'var(--primary-subtle)' }} width="38" height="38" rx="14" />
                        <path style={{ stroke: 'var(--primary)' }} d="M23 12L16 19L23 26" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <h2 className={styles.title}>{t('settings.card.payment.title')}</h2>
            </div>

            <div className={styles.paymentMethod}>
                <div className={styles.methodHeader}>
                    <h3>{t('opay.card.methods')}</h3>
                    {!isAdding && (
                        <Button
                            label={t('opay.card.add')}
                            onClick={handleAddCard}
                            disabled={loading}
                            noAppearance={true}
                            size="medium"
                            backgroundColor="transparent"
                            fontColor="var(--primary)"
                        />
                    )}
                </div>

                {paymentMethods.length > 0 && !isAdding && (
                    <ul className={styles.cardList}>
                        {paymentMethods.map(method => (
                            <li key={method.id} className={styles.cardItem}>
                                <div className={styles.cardDetails}>
                                    <span className={styles.cardBrand}>{brandLabel(method.brand)}</span>
                                    <span className={styles.cardNumber}>{t('opay.card.endingIn')} <bdi dir="ltr">••{method.last4}</bdi></span>
                                    <span className={styles.cardExpiry}>
                                        {t('opay.card.expires')} <bdi dir="ltr">{method.expMonth.toString().padStart(2, '0')}/{method.expYear}</bdi>
                                    </span>
                                    {method.isDefault && <span className={styles.defaultBadge}>{t('opay.card.default')}</span>}
                                </div>
                                <Button
                                    label={t('opay.card.remove')}
                                    noAppearance={true}
                                    size="small"
                                    backgroundColor="transparent"
                                    fontColor="var(--destructive-fg)"
                                    onClick={() => handleRemove(method.id)}
                                    disabled={loading}
                                />
                            </li>
                        ))}
                    </ul>
                )}

                {!isAdding && paymentMethods.length === 0 && !loading && (
                    <p className={styles.emptyState}>{t('opay.card.empty')}</p>
                )}

                {isAdding && clientSecret && (
                    <div className={styles.addCardSection}>
                        <StripeProvider clientSecret={clientSecret}>
                            <StripePaymentForm
                                clientSecret={clientSecret}
                                onSuccess={handlePaymentSuccess}
                                onError={handlePaymentError}
                                submitLabel={t('opay.card.save')}
                            />
                        </StripeProvider>
                        <button type="button" className={styles.cancelLink} onClick={handleCancel}>
                            {t('staff.modal.cancel')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const PaymentSettings: React.FC = () => (
    <StripeProvider>
        <PaymentSettingsInner />
    </StripeProvider>
);

export default PaymentSettings;
